/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type AllHTMLAttributes,
	type ForwardRefExoticComponent,
	type RefAttributes,
} from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx, type SerializedStyles } from '@emotion/react';
// AFP-2532 TODO: Fix automatic suppressions below
// eslint-disable-next-line @atlassian/tangerine/import/entry-points
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	type ExposedDefaultNudgeProps,
	type InternalNudgeProps,
	type NudgeTag,
	type NudgeTooltipPulseProps,
} from './types';

const baseShadow = '0 0 0 3px';
const basePulseColor = token('color.border.accent.purple', colors.P75);
const basePulseShadowColor = token('color.border.accent.purple', 'rgba(101, 84, 192, 1)');
const easing = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';

const pulseAnimation = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'@keyframes pulseKeyframes': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'0%': {
			opacity: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'50%': {
			opacity: 1,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'100%': {
			opacity: 0,
		},
	},
	animationName: 'pulseKeyframes',
	animationDuration: '3000ms',
	animationTimingFunction: easing,
	animationIterationCount: 'infinite',
});

const pseudoStyle = css({
	content: "''",
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
});

export const nudgePulse = ({
	hasPulse,
	pulseBorderRadius,
	pulseColor,
	pulseShadowColor,
}: NudgeTooltipPulseProps): SerializedStyles =>
	css({
		display: 'block',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			position: 'relative',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			'&::before': [
				pseudoStyle,
				{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					borderRadius: `${pulseBorderRadius}px`,
					boxShadow: `${baseShadow} ${pulseColor}`,
					opacity: hasPulse ? 1 : 0,
					transition: 'opacity 0.1s ease-in-out',
					pointerEvents: 'none',
				},
			],
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			'&::after': [
				pseudoStyle,
				{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					borderRadius: `${pulseBorderRadius}px`,
					boxShadow: `${baseShadow} ${pulseColor}, 0 0 11px ${pulseShadowColor}`,
					opacity: 0,
					pointerEvents: 'none',
				},
				hasPulse && pulseAnimation,
			],
		},
	});

export const NudgeTooltipPulse: ForwardRefExoticComponent<
	AllHTMLAttributes<HTMLElement> &
		ExposedDefaultNudgeProps &
		Pick<InternalNudgeProps, 'hasPulse'> &
		RefAttributes<NudgeTag>
> = forwardRef<NudgeTag, React.AllHTMLAttributes<HTMLElement> & NudgeTooltipPulseProps>(
	(
		{
			hasPulse,
			pulseBorderRadius = 3,
			pulseColor = basePulseColor,
			pulseShadowColor = basePulseShadowColor,
			tag: TargetContainer = 'span',
			...rest
		},
		ref,
	) => {
		// Doing a cast because typescript is struggling to narrow the type
		const CastTargetContainer = TargetContainer as React.ElementType;

		return (
			<CastTargetContainer
				css={[
					nudgePulse({
						hasPulse,
						pulseBorderRadius,
						pulseColor,
						pulseShadowColor,
					}),
					inHeritWidthStyles,
				]}
				ref={ref}
				{...rest}
			/>
		);
	},
);

const inHeritWidthStyles = css({ width: 'inherit' });
